import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Lightweight global cache for the active doctrine of the current workspace.
 * Re-fetched on first call per session; refreshed whenever a doctrine is
 * activated (signaled via `window.dispatchEvent(new Event("noam-doctrine-changed"))`).
 *
 * Returns the active doctrine document (with principles, filters, priorities, tone)
 * or `null` if none. UI components feed this to AI endpoints via the `doctrine` payload field.
 */
let _cache = undefined; // undefined = not loaded yet; null/object = loaded

async function fetchActive() {
  try {
    const r = await api.get("/workspace/doctrines");
    const items = r.data?.items || [];
    const active = items.find((d) => d.is_active) || null;
    _cache = active;
    return active;
  } catch {
    _cache = null;
    return null;
  }
}

export function useActiveDoctrine() {
  const [doctrine, setDoctrine] = useState(_cache ?? null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (_cache === undefined) {
      fetchActive().then((d) => mounted.current && setDoctrine(d));
    }
    const onChange = () => fetchActive().then((d) => mounted.current && setDoctrine(d));
    window.addEventListener("noam-doctrine-changed", onChange);
    return () => {
      mounted.current = false;
      window.removeEventListener("noam-doctrine-changed", onChange);
    };
  }, []);

  return doctrine;
}

/** Shape an active doctrine for the AI payload — strips internal fields. */
export function doctrinePayload(d) {
  if (!d) return undefined;
  return {
    title: d.title,
    principles: d.principles || [],
    filters: d.filters || [],
    priorities: d.priorities || [],
    tone: d.tone || "",
  };
}
